import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  CheckCircle2,
  Clock3,
  Loader2,
  Play,
  Scissors,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";

import { useToast } from "../../provider/ToastContext";

import {
  ClipApiError,
  getAnalysisStatus,
  importVideo,
  listAnalysisJobs,
  normalizeJob,
  pollClipExportStatus,
  retrimClip,
  startAnalysis,
  uploadVideo,
} from "./clipApi";

import UploadSection from "./UploadSection";
import WorkspaceSection from "./WorkspaceSection";

const STATUS_POLL_INTERVAL_MS = 3_000;

const PENDING_SOURCE_KEY = "vlogslicer.pendingSource";

const ACTIVE_JOB_KEY = "vlogslicer.activeAnalysisJob";

const ACTIVE_STATUSES = new Set(["queued", "running"]);

/*
 * --------------------------------------------------------
 * Session storage helpers
 * --------------------------------------------------------
 */

function readPendingSource() {
  try {
    const value = sessionStorage.getItem(PENDING_SOURCE_KEY);

    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function readActiveJob() {
  try {
    const value = sessionStorage.getItem(ACTIVE_JOB_KEY);

    return value ? normalizeJob(JSON.parse(value)) : null;
  } catch {
    return null;
  }
}

/*
 * --------------------------------------------------------
 * Job helpers
 * --------------------------------------------------------
 */

function getJobTab(status) {
  if (status === "completed") {
    return "completed";
  }

  if (status === "failed") {
    return "failed";
  }

  return "queue";
}

function clampProgress(value) {
  const progress = Number(value);

  if (!Number.isFinite(progress)) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.round(progress)));
}

/*
 * --------------------------------------------------------
 * Backend-authoritative progress
 * --------------------------------------------------------
 *
 * The analysis worker reports real pipeline checkpoints.
 * The frontend never invents or advances progress on its own.
 *
 * `display_progress` remains only as a compatibility field for components
 * that already read it; its value is always copied from backend progress.
 */

function getDisplayProgress(_previousJob, nextJob) {
  if (nextJob?.status === "completed") {
    return 100;
  }

  return clampProgress(nextJob?.progress);
}

function addDisplayProgress(previousJob, nextJob) {
  if (!nextJob) {
    return null;
  }

  return {
    ...nextJob,

    display_progress: getDisplayProgress(previousJob, nextJob),
  };
}

/*
 * --------------------------------------------------------
 * Insert/update one job without deleting the others
 * --------------------------------------------------------
 */

function upsertJob(jobList, job) {
  if (!job?.id) {
    return jobList;
  }

  const existingIndex = jobList.findIndex(
    (existingJob) => existingJob.id === job.id,
  );

  /*
   * New jobs appear first.
   */
  if (existingIndex === -1) {
    return [job, ...jobList];
  }

  const nextJobs = [...jobList];

  nextJobs[existingIndex] = {
    ...nextJobs[existingIndex],
    ...job,
  };

  return nextJobs;
}

function getVideoId(payload) {
  return Number(payload?.video_id || payload?.video?.id || 0);
}

/*
 * --------------------------------------------------------
 * Source ready card
 * --------------------------------------------------------
 */

function SourceReadyCard({ source, analyzing, onAnalyze, onDismiss }) {
  return (
    <section className="mx-auto w-full max-w-4xl rounded-2xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm sm:flex sm:items-center sm:justify-between sm:gap-6">
      <div className="flex items-start gap-3">
        <span className="rounded-full bg-emerald-100 p-2 text-emerald-700">
          <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
        </span>

        <div>
          <h2 className="font-semibold text-emerald-950">
            Video source is ready
          </h2>

          <p className="mt-1 text-sm text-emerald-800">
            {source.name || `Video ${source.videoId}`} is uploaded. Start
            analysis to generate three suggested clips.
          </p>
        </div>
      </div>

      <div className="mt-4 flex shrink-0 gap-2 sm:mt-0">
        <button
          type="button"
          onClick={onDismiss}
          disabled={analyzing}
          className="rounded-lg border border-emerald-300 px-4 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:opacity-50"
        >
          Dismiss
        </button>

        <button
          type="button"
          onClick={() => onAnalyze(source.videoId)}
          disabled={analyzing}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {analyzing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}

          {analyzing ? "Queueing…" : "Analyze video"}
        </button>
      </div>
    </section>
  );
}

/*
 * ========================================================
 * CreateClips
 * ========================================================
 */

export default function CreateClips({ collapsed }) {
  const { showToast } = useToast();

  /*
   * ------------------------------------------------------
   * Upload / import
   * ------------------------------------------------------
   */

  const [file, setFile] = useState(null);

  const [videoURL, setVideoURL] = useState("");

  const [operation, setOperation] = useState("idle");

  const [sourceReady, setSourceReadyState] = useState(readPendingSource);

  /*
   * ------------------------------------------------------
   * Job state
   *
   * IMPORTANT:
   *
   * jobs starts EMPTY.
   *
   * GET /api/clip/jobs populates this from PostgreSQL.
   * ------------------------------------------------------
   */

  const [jobs, setJobs] = useState([]);

  const [jobsLoading, setJobsLoading] = useState(true);

  const [selectedJob, setSelectedJob] = useState(null);

  const [activeTab, setActiveTab] = useState("queue");

  const [statusError, setStatusError] = useState("");

  /*
   * ------------------------------------------------------
   * Generated clips
   * ------------------------------------------------------
   */

  const [clips, setClips] = useState([]);

  const [clipsLoading, setClipsLoading] = useState(false);

  const [previewClip, setPreviewClip] = useState(null);

  /*
   * ------------------------------------------------------
   * Adjust / retrim state
   * ------------------------------------------------------
   */

  const [adjustingClip, setAdjustingClip] = useState(null);

  const [adjustStartSeconds, setAdjustStartSeconds] = useState("");

  const [adjustEndSeconds, setAdjustEndSeconds] = useState("");

  const [retrimState, setRetrimState] = useState(null);

  const retrimControllerRef = useRef(null);

  /*
   * ------------------------------------------------------
   * Pending source persistence
   * ------------------------------------------------------
   */

  const savePendingSource = useCallback((source) => {
    setSourceReadyState(source);

    if (source) {
      sessionStorage.setItem(PENDING_SOURCE_KEY, JSON.stringify(source));
    } else {
      sessionStorage.removeItem(PENDING_SOURCE_KEY);
    }
  }, []);

  /*
   * ------------------------------------------------------
   * Error handling
   * ------------------------------------------------------
   */

  const reportError = useCallback(
    (error, fallbackMessage) => {
      if (error?.code === "request_cancelled") {
        return;
      }

      const message = error?.message || fallbackMessage;

      if (error instanceof ClipApiError && error.status === 401) {
        window.dispatchEvent(new CustomEvent("vlogslicer:unauthorized"));
      }

      showToast(message, "error");
    },
    [showToast],
  );

  /*
   * ------------------------------------------------------
   * Save selected/current job
   *
   * This updates one job in the existing list rather than
   * replacing the whole list.
   * ------------------------------------------------------
   */

  const saveActiveJob = useCallback((job) => {
    const normalized = job ? normalizeJob(job) : null;

    const normalizedJob = normalized
      ? {
          ...normalized,

          // Always mirror backend progress. This intentionally discards any
          // synthetic display_progress stored by older frontend versions.
          display_progress: getDisplayProgress(null, normalized),
        }
      : null;

    setSelectedJob(normalizedJob);

    if (normalizedJob) {
      setJobs((currentJobs) => upsertJob(currentJobs, normalizedJob));

      sessionStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(normalizedJob));
    } else {
      sessionStorage.removeItem(ACTIVE_JOB_KEY);
    }
  }, []);

  /*
   * ------------------------------------------------------
   * Safe selected-job setter
   *
   * WorkspaceSection / JobTabs may clear the selection.
   * Keep sessionStorage synchronized.
   * ------------------------------------------------------
   */

  const handleSetSelectedJob = useCallback((job) => {
    setSelectedJob(job);

    if (!job) {
      sessionStorage.removeItem(ACTIVE_JOB_KEY);

      setClips([]);

      setPreviewClip(null);

      return;
    }

    sessionStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(normalizeJob(job)));
  }, []);

  /*
   * ======================================================
   * LOAD ALL JOBS WHEN CREATE CLIPS ROUTE MOUNTS
   * ======================================================
   *
   * This is the key fix.
   *
   * GET /api/clip/jobs
   *
   * returns:
   * - queued
   * - running
   * - completed
   * - failed
   *
   * PostgreSQL is the source of truth.
   */

  useEffect(() => {
    const controller = new AbortController();

    let cancelled = false;

    const loadJobs = async () => {
      setJobsLoading(true);

      setStatusError("");

      try {
        const result = await listAnalysisJobs({
          signal: controller.signal,
        });

        if (cancelled) {
          return;
        }

        const storedJob = readActiveJob();

        const serverJobs = Array.isArray(result?.jobs)
          ? result.jobs
              .map((job) => {
                const normalized = normalizeJob(job);

                if (!normalized?.id) {
                  return null;
                }

                /*
                 * PostgreSQL is authoritative for progress.
                 *
                 * Do not restore synthetic progress from sessionStorage;
                 * older frontend versions could have stored values such as
                 * 92% even while the backend was still at 5%.
                 */
                return {
                  ...normalized,

                  display_progress: getDisplayProgress(null, normalized),
                };
              })
              .filter(Boolean)
          : [];

        /*
         * Backend is authoritative.
         */
        setJobs(serverJobs);

        setStatusError("");

        /*
         * ------------------------------------------------
         * Restore previous selection if that job still
         * exists on the server.
         * ------------------------------------------------
         */

        let nextSelected = null;

        if (storedJob?.id) {
          nextSelected =
            serverJobs.find((job) => job.id === storedJob.id) || null;
        }

        /*
         * ------------------------------------------------
         * Otherwise pick the most useful job:
         *
         * 1. Active
         * 2. Completed
         * 3. Failed
         * ------------------------------------------------
         */

        if (!nextSelected) {
          nextSelected =
            serverJobs.find((job) => ACTIVE_STATUSES.has(job.status)) ||
            serverJobs.find((job) => job.status === "completed") ||
            serverJobs.find((job) => job.status === "failed") ||
            null;
        }

        if (nextSelected) {
          setSelectedJob(nextSelected);

          setActiveTab(getJobTab(nextSelected.status));

          sessionStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(nextSelected));
        } else {
          setSelectedJob(null);

          setActiveTab("queue");

          sessionStorage.removeItem(ACTIVE_JOB_KEY);
        }
      } catch (error) {
        if (cancelled || error?.code === "request_cancelled") {
          return;
        }

        console.error("analysis_jobs_load_failed", error);

        setStatusError(
          error?.message || "Your analysis jobs could not be loaded.",
        );

        if (error instanceof ClipApiError && error.status === 401) {
          window.dispatchEvent(new CustomEvent("vlogslicer:unauthorized"));
        }
      } finally {
        if (!cancelled) {
          setJobsLoading(false);
        }
      }
    };

    loadJobs();

    return () => {
      cancelled = true;

      controller.abort();
    };
  }, []);

  /*
   * ======================================================
   * POLL SELECTED JOB
   * ======================================================
   *
   * GET /api/clip/jobs/{jobId}
   *
   * Active job:
   *   polling continues
   *
   * Completed/failed:
   *   one request is made, then polling stops.
   *
   * For completed jobs this request also gives us the
   * three generated suggestions.
   */

  useEffect(() => {
    const jobId = selectedJob?.id;

    if (!jobId) {
      setClips([]);

      return undefined;
    }

    let cancelled = false;

    let pollTimer;

    let requestController;

    const poll = async () => {
      requestController = new AbortController();

      try {
        const result = await getAnalysisStatus(jobId, {
          signal: requestController.signal,
        });

        if (cancelled) {
          return;
        }

        const previousJob = readActiveJob();

        const updatedJob = addDisplayProgress(
          previousJob,
          normalizeJob({
            ...previousJob,
            ...result.job,

            id: result.job?.id || result.job?.job_id || jobId,
          }),
        );

        /*
         * Update only this job.
         *
         * Existing completed/failed jobs remain intact.
         */
        saveActiveJob(updatedJob);

        setClips(Array.isArray(result?.suggestions) ? result.suggestions : []);

        setClipsLoading(false);

        setStatusError("");

        setActiveTab(getJobTab(updatedJob.status));

        /*
         * Continue only for active jobs.
         */
        if (ACTIVE_STATUSES.has(updatedJob.status)) {
          pollTimer = window.setTimeout(poll, STATUS_POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (cancelled || error?.code === "request_cancelled") {
          return;
        }

        setClipsLoading(false);

        setStatusError(
          error?.message || "Analysis status could not be refreshed.",
        );

        if (error instanceof ClipApiError && error.status === 401) {
          window.dispatchEvent(new CustomEvent("vlogslicer:unauthorized"));

          return;
        }

        /*
         * Retry transient polling failure only if the
         * current job is still active.
         */
        if (ACTIVE_STATUSES.has(readActiveJob()?.status)) {
          pollTimer = window.setTimeout(poll, STATUS_POLL_INTERVAL_MS * 2);
        }
      }
    };

    setClipsLoading(true);

    poll();

    return () => {
      cancelled = true;

      window.clearTimeout(pollTimer);

      requestController?.abort();
    };
  }, [selectedJob?.id, saveActiveJob]);

  /*
   * ------------------------------------------------------
   * Close preview with Escape
   * ------------------------------------------------------
   */

  useEffect(() => {
    if (!previewClip) {
      return undefined;
    }

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setPreviewClip(null);
      }
    };

    document.addEventListener("keydown", closeOnEscape);

    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [previewClip]);

  useEffect(() => {
    return () => {
      retrimControllerRef.current?.abort();
    };
  }, []);

  /*
   * ======================================================
   * UPLOAD FILE
   * ======================================================
   */

  const handleFileUpload = async () => {
    if (!file || operation !== "idle") {
      return;
    }

    setOperation("uploading");

    try {
      const result = await uploadVideo(file);

      const videoId = getVideoId(result);

      if (!videoId) {
        throw new Error("Upload succeeded without a video ID.");
      }

      savePendingSource({
        videoId,
        name: file.name,
        source: "upload",
      });

      setFile(null);

      showToast("Video uploaded. It is ready for analysis.", "success");
    } catch (error) {
      reportError(error, "The video could not be uploaded.");
    } finally {
      setOperation("idle");
    }
  };

  /*
   * ======================================================
   * IMPORT YOUTUBE URL
   * ======================================================
   */

  const handleURLImport = async () => {
    const url = videoURL.trim();

    if (!url || operation !== "idle") {
      return;
    }

    setOperation("importing");

    try {
      const result = await importVideo(url);

      const videoId = getVideoId(result);

      if (!videoId) {
        throw new Error("Import succeeded without a video ID.");
      }

      savePendingSource({
        videoId,
        name: "Imported YouTube video",
        source: "url",
      });

      setVideoURL("");

      showToast("Video imported. It is ready for analysis.", "success");
    } catch (error) {
      reportError(error, "The YouTube video could not be imported.");
    } finally {
      setOperation("idle");
    }
  };

  /*
   * ======================================================
   * START ANALYSIS
   * ======================================================
   */

  const handleStartAnalysis = async (videoId) => {
    if (!videoId || operation !== "idle") {
      return;
    }

    setOperation("analyzing");

    try {
      const result = await startAnalysis(videoId);

      const job = normalizeJob({
        ...result,

        id: result?.job_id || result?.id,

        video_id: result?.video_id || videoId,
      });

      if (!job.id) {
        throw new Error("Analysis was queued without a job ID.");
      }

      const jobWithProgress = addDisplayProgress(null, job);

      /*
       * Source card is done.
       */
      savePendingSource(null);

      /*
       * Add this job to the existing server-loaded list.
       *
       * Completed and failed history is preserved.
       */
      saveActiveJob(jobWithProgress);

      setClips([]);

      setPreviewClip(null);

      setStatusError("");

      setActiveTab("queue");

      showToast(
        "Analysis queued. Your clips will be ready in minutes.",
        "success",
      );
    } catch (error) {
      reportError(error, "Analysis could not be started.");
    } finally {
      setOperation("idle");
    }
  };

  /*
   * ======================================================
   * SELECT EXISTING JOB
   * ======================================================
   */

  const handleSelectJob = useCallback((job) => {
    if (!job?.id) {
      return;
    }

    const normalized = normalizeJob(job);

    setSelectedJob(normalized);

    sessionStorage.setItem(ACTIVE_JOB_KEY, JSON.stringify(normalized));

    /*
     * Prevent old job's clips from flashing while the
     * selected job is being fetched.
     */
    setClips([]);

    setPreviewClip(null);

    setStatusError("");

    setActiveTab(getJobTab(normalized.status));
  }, []);

  /*
   * ======================================================
   * RETRY FAILED JOB
   * ======================================================
   */

  const handleRetry = () => {
    if (!selectedJob?.video_id) {
      return;
    }

    // Return the promise so JobDetailsPanel can keep its Retry button
    // disabled/spinning until the new analysis request finishes.
    return handleStartAnalysis(selectedJob.video_id);
  };

  /*
   * ======================================================
   * REFRESH COMPLETED CLIP LINKS
   * ======================================================
   */

  const handleRefreshResults = async () => {
    if (!selectedJob?.id) {
      return;
    }

    setClipsLoading(true);

    try {
      const result = await getAnalysisStatus(selectedJob.id);

      const updatedJob = addDisplayProgress(
        selectedJob,
        normalizeJob({
          ...selectedJob,
          ...result.job,

          id: result.job?.id || result.job?.job_id || selectedJob.id,
        }),
      );

      saveActiveJob(updatedJob);

      setClips(Array.isArray(result?.suggestions) ? result.suggestions : []);

      setStatusError("");

      showToast("Preview links refreshed.", "success");
    } catch (error) {
      reportError(error, "Preview links could not be refreshed.");
    } finally {
      setClipsLoading(false);
    }
  };

  /*
   * ======================================================
   * ADJUST / RETRIM ONE CLIP
   * ======================================================
   */

  const handleOpenAdjustClip = useCallback((clip) => {
    if (!clip) {
      return;
    }

    const startSeconds = Number(clip.start_seconds ?? clip.start_sec ?? 0);

    const endSeconds = Number(clip.end_seconds ?? clip.end_sec ?? 0);

    setPreviewClip(null);

    setAdjustingClip(clip);

    setAdjustStartSeconds(
      Number.isFinite(startSeconds) ? String(startSeconds) : "0",
    );

    setAdjustEndSeconds(Number.isFinite(endSeconds) ? String(endSeconds) : "");

    setRetrimState(null);
  }, []);

  const retrimBusy =
    retrimState?.status === "queued" || retrimState?.status === "running";

  const handleCloseAdjustClip = useCallback(() => {
    if (retrimState?.status === "queued" || retrimState?.status === "running") {
      return;
    }

    setAdjustingClip(null);
    setRetrimState(null);
  }, [retrimState?.status]);

  const handleSubmitRetrim = async () => {
    if (!adjustingClip || retrimBusy) {
      return;
    }

    const suggestionId = String(
      adjustingClip.id || adjustingClip.clip_id || "",
    ).trim();

    const startSeconds = Number(adjustStartSeconds);
    const endSeconds = Number(adjustEndSeconds);
    const durationSeconds = endSeconds - startSeconds;

    if (!suggestionId) {
      showToast("This clip does not have a suggestion ID.", "error");
      return;
    }

    if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) {
      showToast("Enter a valid start and end time.", "error");
      return;
    }

    if (startSeconds < 0) {
      showToast("Start time cannot be negative.", "error");
      return;
    }

    if (endSeconds <= startSeconds) {
      showToast("End time must be greater than start time.", "error");
      return;
    }

    if (durationSeconds < 5 || durationSeconds > 90) {
      showToast("Adjusted clips must be between 5 and 90 seconds.", "error");
      return;
    }

    retrimControllerRef.current?.abort();

    const controller = new AbortController();

    retrimControllerRef.current = controller;

    setRetrimState({
      suggestionId,
      exportId: "",
      status: "queued",
      progress: 0,
      message: "Queueing adjusted clip…",
      lastFailedStage: "",
    });

    try {
      const queued = await retrimClip(suggestionId, startSeconds, endSeconds, {
        signal: controller.signal,
      });

      const exportId = String(queued?.export_id || "").trim();

      if (!exportId) {
        throw new ClipApiError(
          "The adjusted clip was queued without an export ID.",
          {
            code: "missing_export_id",
            details: queued,
          },
        );
      }

      setRetrimState({
        suggestionId,
        exportId,
        status: queued.status || "queued",
        progress: clampProgress(queued.progress),
        message: queued.message || "Adjusted clip queued.",
        lastFailedStage: "",
      });

      const completedExport = await pollClipExportStatus(exportId, {
        signal: controller.signal,

        onUpdate: (clipExport) => {
          setRetrimState({
            suggestionId,
            exportId,
            status: clipExport.status,
            progress: clampProgress(clipExport.progress),
            message:
              clipExport.message ||
              (clipExport.status === "running"
                ? "Generating adjusted clip…"
                : "Adjusted clip queued."),
            lastFailedStage: clipExport.last_failed_stage || "",
          });
        },
      });

      if (completedExport.status === "failed") {
        const failureMessage =
          completedExport.message ||
          "The adjusted clip could not be generated.";

        setRetrimState({
          suggestionId,
          exportId,
          status: "failed",
          progress: clampProgress(completedExport.progress),
          message: failureMessage,
          lastFailedStage: completedExport.last_failed_stage || "",
        });

        showToast(failureMessage, "error");
        return;
      }

      const nextPreviewURL =
        completedExport.preview?.url || completedExport.preview_url || "";

      const nextThumbnailURL =
        completedExport.thumbnail?.url || completedExport.thumbnail_url || "";

      if (!nextPreviewURL) {
        throw new ClipApiError(
          "The adjusted clip completed without a preview URL.",
          {
            code: "missing_adjusted_preview_url",
            details: completedExport,
          },
        );
      }

      setClips((currentClips) =>
        currentClips.map((clip) => {
          const clipSuggestionId = String(
            clip?.id || clip?.clip_id || "",
          ).trim();

          if (clipSuggestionId !== suggestionId) {
            return clip;
          }

          const currentPreview =
            clip?.preview &&
            typeof clip.preview === "object" &&
            !Array.isArray(clip.preview)
              ? clip.preview
              : {};

          const currentThumbnail =
            clip?.thumbnail &&
            typeof clip.thumbnail === "object" &&
            !Array.isArray(clip.thumbnail)
              ? clip.thumbnail
              : {};

          const currentDownload =
            clip?.download &&
            typeof clip.download === "object" &&
            !Array.isArray(clip.download)
              ? clip.download
              : {};

          return {
            ...clip,

            start_seconds: completedExport.start_seconds,
            start_sec: completedExport.start_seconds,

            end_seconds: completedExport.end_seconds,
            end_sec: completedExport.end_seconds,

            duration_seconds: completedExport.duration_seconds,

            preview: {
              ...currentPreview,
              ...(completedExport.preview || {}),
              url: nextPreviewURL,
            },

            thumbnail: {
              ...currentThumbnail,
              ...(completedExport.thumbnail || {}),
              url: nextThumbnailURL,
            },

            preview_url: nextPreviewURL,
            video_url: nextPreviewURL,
            url: nextPreviewURL,

            thumbnail_url: nextThumbnailURL,

            /*
             * The backend currently returns an inline signed preview URL for
             * retrim exports, not a dedicated attachment URL. Clear the old
             * suggestion download link so we never download the pre-adjusted
             * clip by mistake.
             */
            download: {
              ...currentDownload,
              url: "",
            },

            download_url: "",
            download_signed_url: "",
            signed_download_url: "",

            adjusted_export_id: completedExport.id,
            is_adjusted: true,
          };
        }),
      );

      setRetrimState({
        suggestionId,
        exportId,
        status: "completed",
        progress: 100,
        message: completedExport.message || "Adjusted clip ready.",
        lastFailedStage: "",
      });

      setAdjustingClip(null);

      showToast("Adjusted clip is ready.", "success");
    } catch (error) {
      if (error?.code === "request_cancelled") {
        return;
      }

      setRetrimState((currentState) => ({
        ...(currentState || {
          suggestionId,
          exportId: "",
          progress: 0,
        }),
        status: "failed",
        message: error?.message || "The clip could not be adjusted.",
      }));

      reportError(error, "The clip could not be adjusted.");
    } finally {
      if (retrimControllerRef.current === controller) {
        retrimControllerRef.current = null;
      }
    }
  };

  /*
   * ------------------------------------------------------
   * Workspace state
   * ------------------------------------------------------
   */

  const getJobStateUI = (job) => {
    if (!job) {
      return "empty";
    }

    if (job.status === "failed") {
      return "failed";
    }

    if (job.status === "completed") {
      return "completed";
    }

    return "processing";
  };

  /*
   * ------------------------------------------------------
   * Processing timeline
   * ------------------------------------------------------
   *
   * These thresholds mirror the real backend checkpoints:
   *
   *   0   queued
   *   5   started
   *   10  downloading source
   *   15  source downloaded
   *   20  video validated
   *   25  extracting audio
   *   30  audio ready
   *   35  transcribing
   *   55  transcription complete
   *   60  selecting clips
   *   72  selection complete
   *   75  generating previews
   *   80/84/88 preview progress
   *   90  uploading assets
   *   95  assets uploaded
   *   97  saving results
   *   100 completed
   */

  const renderTimeline = () => {
    if (!selectedJob) {
      return null;
    }

    const failed = selectedJob.status === "failed";
    const progress = clampProgress(
      selectedJob.display_progress ?? selectedJob.progress,
    );

    const stages = [
      {
        label: "Analysis queued",
        threshold: 0,
        icon: Clock3,
      },
      {
        label: "Preparing video",
        threshold: 5,
        icon: Sparkles,
      },
      {
        label: "Preparing audio",
        threshold: 25,
        icon: Sparkles,
      },
      {
        label: "Transcribing",
        threshold: 35,
        icon: Sparkles,
      },
      {
        label: "Selecting clips",
        threshold: 60,
        icon: Sparkles,
      },
      {
        label: "Generating previews",
        threshold: 75,
        icon: Sparkles,
      },
      {
        label: "Finalizing clips",
        threshold: 90,
        icon: Sparkles,
      },
      {
        label: "Clips ready",
        threshold: 100,
        icon: CheckCircle2,
      },
    ];

    let currentIndex = 0;

    if (selectedJob.status === "completed") {
      currentIndex = stages.length - 1;
    } else {
      for (let index = 0; index < stages.length; index += 1) {
        if (progress >= stages[index].threshold) {
          currentIndex = index;
        }
      }
    }

    return (
      <div className="mt-6 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {stages.map((stage, index) => {
          const current = index === currentIndex;
          const done =
            selectedJob.status === "completed" ||
            (!failed && index < currentIndex);

          const Icon = failed && current ? XCircle : stage.icon;

          return (
            <div
              key={stage.label}
              className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-xs ${
                failed && current
                  ? "border-red-200 bg-red-50"
                  : current
                    ? "border-indigo-200 bg-indigo-50"
                    : done
                      ? "border-emerald-100 bg-emerald-50/70"
                      : "border-gray-100 bg-white"
              }`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                  failed && current
                    ? "bg-red-100 text-red-600"
                    : done
                      ? "bg-emerald-100 text-emerald-600"
                      : current
                        ? "bg-indigo-100 text-indigo-600"
                        : "bg-gray-100 text-gray-400"
                }`}
              >
                <Icon
                  className={`h-3.5 w-3.5 ${
                    current && !failed && selectedJob.status !== "completed"
                      ? "animate-pulse"
                      : ""
                  }`}
                />
              </span>

              <span
                className={
                  failed && current
                    ? "font-semibold text-red-700"
                    : current
                      ? "font-semibold text-indigo-800"
                      : done
                        ? "font-medium text-emerald-700"
                        : "text-gray-500"
                }
              >
                {failed && current ? "Analysis failed" : stage.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  /*
   * ------------------------------------------------------
   * Preview URL
   * ------------------------------------------------------
   */

  const previewURL =
    previewClip?.preview_url ||
    previewClip?.video_url ||
    previewClip?.url ||
    "";

  /*
   * ======================================================
   * RENDER
   * ======================================================
   */

  return (
    <main
      className={`min-h-screen bg-gray-50 px-4 py-6 transition-[margin] duration-300 sm:px-6 sm:py-8 ${
        collapsed ? "lg:ml-20" : "lg:ml-72"
      }`}
    >
      <div className="mx-auto max-w-7xl space-y-8">
        {/* Header */}

        <header className="text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
            <Sparkles className="h-3.5 w-3.5" />
            AI clip studio
          </div>

          <h1 className="text-3xl font-bold tracking-tight text-gray-950 sm:text-4xl">
            Turn long videos into useful short clips
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-gray-600 sm:text-base">
            Upload once and let VlogSlicer find, caption and prepare three
            ready-to-review vertical clips.
          </p>
        </header>

        {/* Upload/import */}

        <UploadSection
          file={file}
          setFile={setFile}
          videoURL={videoURL}
          setVideoURL={setVideoURL}
          operation={operation}
          onFileUpload={handleFileUpload}
          onURLImport={handleURLImport}
        />

        {/* Source ready */}

        {sourceReady && (
          <SourceReadyCard
            source={sourceReady}
            analyzing={operation === "analyzing"}
            onAnalyze={handleStartAnalysis}
            onDismiss={() => savePendingSource(null)}
          />
        )}

        {/* Jobs / results */}

        <WorkspaceSection
          jobs={jobs}
          jobsLoading={jobsLoading}
          jobsError={statusError}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedJob={selectedJob}
          setSelectedJob={handleSetSelectedJob}
          clips={clips}
          clipsLoading={clipsLoading}
          handleSelectJob={handleSelectJob}
          handleRetry={handleRetry}
          handleRefreshResults={handleRefreshResults}
          renderTimeline={renderTimeline}
          getJobStateUI={getJobStateUI}
          setPreviewClip={setPreviewClip}
          onAdjustClip={handleOpenAdjustClip}
          retrimState={retrimState}
        />
      </div>

      {/* Adjust / retrim modal */}

      {adjustingClip && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={`Adjust ${adjustingClip.title || "clip"}`}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseAdjustClip();
            }
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
                  <Scissors className="h-4 w-4" />
                </span>

                <div>
                  <h2 className="font-semibold text-gray-950">
                    Adjust clip timing
                  </h2>

                  <p className="mt-0.5 text-xs text-gray-500">
                    Choose a new 5–90 second range from the original video.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleCloseAdjustClip}
                disabled={retrimBusy}
                aria-label="Close adjust clip"
                className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-semibold text-gray-700">
                  Start seconds
                </span>

                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={adjustStartSeconds}
                  onChange={(event) =>
                    setAdjustStartSeconds(event.target.value)
                  }
                  disabled={retrimBusy}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-100"
                />
              </label>

              <label className="block">
                <span className="text-xs font-semibold text-gray-700">
                  End seconds
                </span>

                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={adjustEndSeconds}
                  onChange={(event) => setAdjustEndSeconds(event.target.value)}
                  disabled={retrimBusy}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-gray-100"
                />
              </label>
            </div>

            <div className="mt-4 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
              Duration:{" "}
              <span className="font-semibold text-gray-900">
                {Number.isFinite(
                  Number(adjustEndSeconds) - Number(adjustStartSeconds),
                )
                  ? Math.max(
                      0,
                      Number(adjustEndSeconds) - Number(adjustStartSeconds),
                    ).toFixed(1)
                  : "0.0"}
                s
              </span>
            </div>

            {retrimBusy && (
              <div className="mt-5 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-indigo-600" />

                    <span className="truncate text-sm font-semibold text-indigo-900">
                      {retrimState?.message || "Generating adjusted clip…"}
                    </span>
                  </div>

                  <span className="shrink-0 text-xs font-bold text-indigo-700">
                    {clampProgress(retrimState?.progress)}%
                  </span>
                </div>

                <div
                  className="mt-3 h-2 overflow-hidden rounded-full bg-white"
                  role="progressbar"
                  aria-label="Adjusted clip generation progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={clampProgress(retrimState?.progress)}
                >
                  <div
                    className="h-full rounded-full bg-indigo-600 transition-[width] duration-500"
                    style={{
                      width: `${clampProgress(retrimState?.progress)}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {retrimState?.status === "failed" && (
              <div
                role="alert"
                className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {retrimState.message ||
                  "The adjusted clip could not be generated."}

                {retrimState.lastFailedStage && (
                  <p className="mt-1 text-xs font-medium">
                    Failed stage: {retrimState.lastFailedStage}
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCloseAdjustClip}
                disabled={retrimBusy}
                className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmitRetrim}
                disabled={retrimBusy}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {retrimBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Scissors className="h-4 w-4" />
                )}

                {retrimBusy ? "Generating…" : "Generate adjusted clip"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}

      {previewClip && previewURL && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={previewClip.title || "Clip preview"}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setPreviewClip(null);
            }
          }}
        >
          <div className="relative w-full max-w-sm">
            <div className="mb-3 flex items-center justify-between gap-4 text-white">
              <p className="truncate text-sm font-semibold">
                {previewClip.title || "Clip preview"}
              </p>

              <button
                type="button"
                onClick={() => setPreviewClip(null)}
                aria-label="Close clip preview"
                className="rounded-full bg-white/10 p-2 transition hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="aspect-[9/16] overflow-hidden rounded-2xl bg-black shadow-2xl">
              <video
                key={previewURL}
                src={previewURL}
                controls
                autoPlay
                playsInline
                preload="metadata"
                className="h-full w-full object-contain"
              >
                Your browser does not support HTML5 video.
              </video>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
